import { View, Platform } from 'react-native'
import SafePressable from '@ui/buttons/SafePressable'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import { OutlineAnimated } from '@ui/elements/Outline'
import { borderRadius, colorOption, colorOutline } from '@order/Package/styles'
import Head from '@order/Package/Head'
import Border from '@ui/effects/Border'
import { useMaxWindowDimensions } from '@ui/theme/useRWD'
import { gap } from '@ui/theme/values'
import DiscountPackage from '@order/PricePackage/DiscountPackage'
import PricePackage from '@order/PricePackage'
import Typography from '@ui/typography'
import { optionGradient, optionTextColor } from '@order/PricePackage/styles'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'
import Button from '@ui/buttons/Button'
import BlurEffect from '@ui/effects/BlurEffect'
import cn from '@ui/utils/cn'

import Attribute from '@ui/elements/Attribute'
import { itemsRow, width } from '@ui/elements/Attribute/styles'
import { PackageAttributesTileProps as Props } from './types'

const BLUR = Platform.select({ default: 100, web: 60 })

export default function PackageAttributesTile(props: Props) {
  const {
    tier = 'default',
    title,
    attributes,
    price,
    discount,
    isRecommended,
    recommendedText,
    onPress,
    onAttributePress,
    tileRef,
    onLayout,
  } = props

  const { t } = useTranslation(['attributes'])
  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const { width: widthScreen } = useMaxWindowDimensions()
  const gapItems = (widthScreen - 4 * gap - itemsRow * width) / (itemsRow - 1)

  const recommended = isRecommended && recommendedText

  const stylePressedOpacityGradient = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animation.value, [0, 1], [1, 0.75]),
    }
  })

  return (
    <SafePressable onPress={onPress} onPressIn={onPressIn} onPressOut={onPressOut}>
      <Animated.View onLayout={onLayout} ref={tileRef} className="relative">
        {isRecommended && (
          <OutlineAnimated
            size={4}
            borderRadius={borderRadius}
            color={colorOutline[tier]}
          />
        )}
        <View
          className="flex flex-col overflow-hidden"
          style={{ borderRadius }}
        >
          <Head
            size="default"
            tier={tier}
            title={title}
            isRecommended={isRecommended}
          />
          <View className="flex flex-col items-center justify-center gap-0 bg-white">
            <View className="flex w-full flex-1 flex-col items-center justify-center gap-0">
              <View className="absolute h-full w-full overflow-hidden">
                <Gradient
                  colors={[colors.white, colors.white]}
                  start={[0.5, 0]}
                  end={[0.5, 1]}
                />
                <View
                  className="absolute"
                  style={{
                    width: widthScreen,
                    height: 128,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <Gradient
                    colors={optionGradient[colorOption[tier]]}
                    start={[0.5, 0]}
                    end={[0.5, 1]}
                  />
                </View>
                {Platform.select({
                  default: (
                    <BlurEffect
                      tint="regular"
                      intensity={BLUR}
                      className="left-0 h-full"
                    />
                  ),
                  web: (
                    <div
                      className="absolute left-0 top-0 h-full w-full"
                      style={{
                        backdropFilter: `blur(${BLUR}px)`,
                      }}
                    />
                  ),
                })}
                <Gradient
                  colors={[colors.white, colors.white50]}
                  start={[0.5, 0]}
                  end={[0.5, 1]}
                  style={[stylePressedOpacityGradient]}
                />
                <Gradient
                  colors={[colors.white25, colors.white, colors.white]}
                  start={[0, 0.5]}
                  end={[1, 0.5]}
                  style={[stylePressedOpacityGradient]}
                />
              </View>
              <View
                className="flex flex-row flex-wrap items-center justify-center px-8"
                style={{
                  gap: gapItems,
                  paddingVertical: attributes?.length ? 16 : 0,
                }}
              >
                {attributes?.map((attribute, index) => (
                  <View
                    key={attribute.id}
                    style={{
                      marginTop: index >= itemsRow ? -gapItems + 16 : 0,
                    }}
                  >
                    <Attribute
                      text={t(`attributes:${attribute.name}.label`)}
                      image={attribute.icon?.url}
                      onPress={() => onAttributePress?.(index)}
                    />
                  </View>
                ))}
              </View>
              <View
                className="flex w-full flex-1 flex-row"
                style={{ minHeight: 112 }}
              >
                <Border
                  className={cn(
                    'h-full w-full rounded-none border-0',
                    recommended ? 'border-y' : 'border-t'
                  )}
                />
                <View className="flex w-full flex-1 flex-row items-end justify-between p-8">
                  <View className="flex flex-row items-center justify-center gap-8">
                    <PricePackage
                      hideDiscountPrefix
                      price={price}
                      colorOption={colorOption[tier]}
                    />
                    {discount && <DiscountPackage discount={discount} />}
                  </View>
                  <Button
                    noninteractive
                    variant="outlined"
                    label={t('ui:select')}
                    animation={animation}
                  />
                </View>
              </View>
            </View>
            {recommended && (
              <View className="flex w-full flex-col items-start justify-start gap-0">
                <Gradient
                  colors={[optionTextColor[colorOption[tier]], colors.white]}
                  start={[0.5, 0]}
                  end={[0.5, 1]}
                  style={{ opacity: 0.15 }}
                />
                <Typography variant="SM" className="p-8 text-left color-desc">
                  {recommendedText}
                </Typography>
              </View>
            )}
          </View>
          <Border style={{ borderRadius }} />
        </View>
      </Animated.View>
    </SafePressable>
  )
}
